import { timeZone } from 'app/utils/helpers';
import dayjs from 'dayjs';
import { getUserInfo } from 'store/common/actions';
import store from 'store/index';

import { errorToast, successToast } from './toast';

function debounce(cb, delay = 2000) {
  let timeoutRef;

  const cancel = () => {
    clearTimeout(timeoutRef);
  };

  const debounced = (...args) => {
    const context = this;

    if (timeoutRef) {
      clearTimeout(timeoutRef);
    }
    timeoutRef = setTimeout(() => cb.apply(context, args), delay);
  };

  debounced.cancel = cancel;

  return debounced;
}

function throttle(cb, delay = 1000) {
  let timeoutRef;
  let flag = true;

  return (...args) => {
    if (flag) {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }

      flag = false;

      cb.apply(this, args);

      timeoutRef = setTimeout(() => {
        flag = true;
      }, delay);
    }
  };
}

function cropText(text, showStartNum = 4, showEndNum) {
  return `${text.slice(0, showStartNum)}...${text.slice(
    -(showEndNum ?? showStartNum)
  )}`;
}

function handleFileDownload(response) {
  const { headers, data } = response;
  const filename =
    headers['content-disposition'].split('file_name=')[1] ||
    headers['content-disposition'].split('filename=')[1];
  const blob = new Blob([data], { type: headers['content-type'] });

  const a = document.createElement('a');
  a.href = window.URL.createObjectURL(blob);
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function downloadCSV(csvData, filename) {
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'data.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function getSSSelectId(id) {
  return `select-${id}`;
}

function arraySwap(arr, idx1, idx2) {
  [arr[idx1], arr[idx2]] = [arr[idx2], arr[idx1]];
}

async function withDefaultUiHandler(
  fn,
  { propagateError = true, successMsg, errorMsg, onError } = {}
) {
  try {
    const data = await fn();

    if (successMsg) {
      successToast(successMsg);
    }

    return data;
  } catch (error) {
    const errorMessage = errorMsg || error.message;

    if (errorMessage) {
      errorToast(errorMessage);
    }

    onError?.();

    if (propagateError) {
      throw error;
    }
  }
}

function isWindow() {
  return typeof window !== 'undefined';
}

function isNumber(d) {
  return Number(d) == d;
}

const isInteger = value => {
  return Number.isInteger(Number(value));
};

const convertDataType = value => {
  return isNumber(value) ? Number(value) : value;
};

function setUserInfo() {
  const { getState, dispatch } = store;

  const { userInfo } = getState().common;

  if (!Object.keys(userInfo).length) {
    dispatch(getUserInfo());
  }
}

function snakeCaseToWords(str) {
  if (typeof str !== 'string') {
    return str;
  }

  return str
    .replace(/[^a-zA-Z0-9]+(.)/g, m => m.replace('_', ' '))
    .replace(/\b([a-z])/g, m => m.toUpperCase());
}

function capitalize(str) {
  return str.replace(/^./, str[0].toUpperCase());
}

function generateIntArrayOfRange(length) {
  return Array.from({ length }, (_, i) => i + 1);
}

function convertDateTo24Format(isoString) {
  if (!isoString) {
    return isoString;
  }
  return dayjs(isoString).tz(timeZone).format('DD MMM YYYY | HH:mm');
}

const PIPE_FUNC_STATE = { fns: null };

async function pipeFunc(...fns) {
  PIPE_FUNC_STATE.fns = fns;

  try {
    for (const fn of fns) {
      await Promise.resolve(fn());
    }

    PIPE_FUNC_STATE.fns = null;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
}

function getHostName() {
  return window.location.hostname;
}

function toCamelCase(snakeStr) {
  return snakeStr.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export {
  arraySwap,
  capitalize,
  convertDataType,
  convertDateTo24Format,
  cropText,
  debounce,
  downloadCSV,
  generateIntArrayOfRange,
  getHostName,
  getSSSelectId,
  handleFileDownload,
  isInteger,
  isNumber,
  isWindow,
  PIPE_FUNC_STATE,
  pipeFunc,
  setUserInfo,
  snakeCaseToWords,
  throttle,
  toCamelCase,
  wait,
  withDefaultUiHandler,
};
